import * as changeCase from 'change-case';
import dayjs from 'dayjs';
import {
  ArrayParam,
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
// import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';

import React, { useState } from 'react';

import { useAnalytics } from '@contexts/analyticsContext';

import {
  areArraysIdentical,
  formatNumber,
  formatRawData,
  getComparator,
  stableSort,
} from '../_utils/chartHelpers';
import { ObjectParam } from '../_utils/filtersHelpers';
import CTRMetricPicker from './ctrMetricPicker';
import DimensionMetricPicker from './dimensionMetricPicker';
import { MAX_CHART_LINES } from './dynamicChart';

const noop = () => {};

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label='first page'
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label='previous page'
      >
        {theme.direction === 'rtl' ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label='next page'
      >
        {theme.direction === 'rtl' ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label='last page'
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

function HeaderTypography({
  children,
  underline,
  tooltip,
  tooltipText,
  ...props
}) {
  return tooltip ? (
    <Tooltip title={tooltipText} arrow>
      <Typography
        {...props}
        sx={{
          textDecoration: underline ? 'underline' : 'none',
          textDecorationStyle: underline ? 'dashed' : undefined,
          textDecorationThickness: underline ? '0.1rem' : undefined,
          textDecorationColor: underline
            ? props.sx.color || 'inherit'
            : undefined,
          ...props.sx,
        }}
      >
        {children}
      </Typography>
    </Tooltip>
  ) : (
    <Typography
      {...props}
      sx={{
        textDecoration: underline ? 'underline' : undefined,
        textDecorationStyle: underline ? 'dashed' : undefined,
        textDecorationThickness: underline ? '0.1rem' : undefined,
        textDecorationColor: underline
          ? props.sx.color || 'inherit'
          : undefined,
        ...props.sx,
      }}
    >
      {children}
    </Typography>
  );
}

function ItemValue({
  value,
  noSecondary = false,
  nullishText = '',
  direction = 'row',
  total = false,
}) {
  if (value !== null && typeof value === 'object' && value.main) {
    return (
      <Box
        component='span'
        sx={{
          display: 'inline-flex',
          gap: 1,
          alignItems: direction === 'column' ? 'end' : 'center',
          justifyContent: direction === 'column' ? 'right' : undefined,
          flexDirection: direction === 'column' ? 'column' : 'row',
        }}
      >
        <Typography variant='body2'>{value.main ?? nullishText}</Typography>
        {value.secondary && !noSecondary && (
          <Typography variant='body2' sx={{ color: 'gray' }}>
            ({value.secondary}%{total ? ' of total' : ''})
          </Typography>
        )}
      </Box>
    );
  }

  return <Typography variant='body2'>{value ?? nullishText}</Typography>;
}

// Main dynamic table component
const AnalyticsTable = ({
  selectedRowsForChart,
  dimensions,
  metrics: metricsFromProps,
  reportConfig,
  data: dataFromProps = { rows: [], formattedRows: [] },
  rawData: rawDataFromProps = { rows: [], formattedRows: [] },
  onLiveDataChange = noop,
  onCheckboxChange = noop,
  onPageChange = noop,
  onRowsPerPageChange = noop,
}) => {
  const {
    metadata,
    // loadingMetadata,
    // metadataError,
  } = useAnalytics();
  // const [page, setPage] = useState(0);
  // const [rowsPerPage, setRowsPerPage] = useState(10);
  // const [selectedDimension, setSelectedDimension] = useState(
  //   dimensions?.[0] || null,
  // );
  // const [ctrSelectedViews, setCTR_SelectedViews] = useState([]);
  // const [ctrSelectedClicks, setCTR_SelectedClicks] = useState([]);
  // const [order, setOrder] = React.useState('asc');
  // const [orderBy, setOrderBy] = React.useState(null);
  // const [withCTR, setWithCTR] = useState(null);
  const [data, setData] = useState({ rows: [], formattedRows: [] });
  const [anchorEl, setAnchorEl] = useState(null);
  const [columnIndex, setColumnIndex] = useState(null);
  const [headerMetricSelection, setHeaderMetricSelection] = useState(null);
  const transientInitDataDone = React.useRef(null);
  const [query, setQuery] = useQueryParams({
    page: withDefault(NumberParam, 0),
    rowsPerPage: withDefault(NumberParam, 10),
    order: withDefault(StringParam, 'asc'),
    orderBy: StringParam,
    selectedDimension: ObjectParam,
    ctrSelectedViews: ArrayParam,
    ctrSelectedClicks: ArrayParam,
    withCTR: withDefault(BooleanParam, true),
  });
  const page = query.page ?? 0;
  const setPage = (value) =>
    query?.page !== value ? setQuery({ page: value }, 'replaceIn') : undefined;

  const rowsPerPage = query.rowsPerPage ?? 10;
  const setRowsPerPage = (value) =>
    query?.rowsPerPage !== value
      ? setQuery({ rowsPerPage: value }, 'replaceIn')
      : undefined;

  const order = query.order ?? 'asc';
  const setOrder = (value) =>
    query?.order !== value
      ? setQuery({ order: value }, 'replaceIn')
      : undefined;

  const orderBy = query.orderBy ?? null;
  const setOrderBy = (value) =>
    query?.orderBy !== value
      ? setQuery({ orderBy: value }, 'replaceIn')
      : undefined;

  const selectedDimension = query.selectedDimension ?? dimensions?.[0] ?? null;
  const setSelectedDimension = (value) =>
    query?.selectedDimension !== value
      ? setQuery({ selectedDimension: value }, 'replaceIn')
      : undefined;

  const ctrSelectedViews = query.ctrSelectedViews ?? [];
  const setCTR_SelectedViews = (value) =>
    query?.ctrSelectedViews !== value
      ? setQuery({ ctrSelectedViews: value }, 'replaceIn')
      : undefined;

  const ctrSelectedClicks = query.ctrSelectedClicks ?? [];
  const setCTR_SelectedClicks = (value) =>
    query?.ctrSelectedClicks !== value
      ? setQuery({ ctrSelectedClicks: value }, 'replaceIn')
      : undefined;

  const withCTR = query.withCTR ?? true;
  const setWithCTR = (value) =>
    query?.withCTR !== value
      ? setQuery({ withCTR: value }, 'replaceIn')
      : undefined;

  const ctrDataEligible = React.useMemo(() => {
    if (!dataFromProps?.rawData) return false;
    const [data_] = dataFromProps.rawData;
    const dimensionHeaders = data_.dimensionHeaders.map(
      (header) => header.name,
    );
    const metricHeaders = data_.metricHeaders.map((header) => header.name);
    const hasEventData =
      dimensionHeaders.includes('eventName') &&
      metricHeaders.includes('eventCount');
    const selectedDimName = selectedDimension?.name || selectedDimension;
    return hasEventData && selectedDimName;
  }, [dataFromProps?.rawData, selectedDimension]);

  React.useEffect(() => {
    return () => {
      transientInitDataDone.current = undefined;
    };
  }, []);

  React.useEffect(() => {
    if (!transientInitDataDone.current && dataFromProps) {
      setData(dataFromProps);
      transientInitDataDone.current = true;
    }
  }, [dataFromProps]);

  React.useEffect(() => {
    if (reportConfig?.orderBys?.length) {
      const orderBy_ = reportConfig.orderBys.find(
        (it) => it?.metric?.metricName,
      );
      setOrderBy(orderBy_?.metric?.metricName || null);
      setOrder(orderBy_?.desc ? 'desc' : 'asc');
    }
  }, [reportConfig?.orderBys?.length]);

  React.useEffect(() => {
    if (!ctrDataEligible && withCTR) {
      setWithCTR(false);
      return;
    }

    if (withCTR !== null && selectedDimension && dataFromProps?.rawData) {
      const selectedEvent = {
        metricName: null,
        value: headerMetricSelection?.name || headerMetricSelection,
      };
      const newData = formatRawData({
        viewEvents: ctrSelectedViews,
        clickEvents: ctrSelectedClicks,
        rawData: rawDataFromProps,
        selectedDimension,
        selectedEvent,
        withCTR,
      });

      setData(newData);
      onLiveDataChange(newData);
    }
  }, [
    ctrDataEligible,
    withCTR,
    selectedDimension,
    ctrSelectedViews,
    ctrSelectedClicks,
  ]);

  React.useEffect(() => {
    if (
      dataFromProps.defaultViewEvents &&
      dataFromProps.defaultViewEvents !== null &&
      !areArraysIdentical(ctrSelectedViews, dataFromProps.defaultViewEvents)
    ) {
      setCTR_SelectedViews(dataFromProps.defaultViewEvents);
    }
  }, [dataFromProps.defaultViewEvents]);

  React.useEffect(() => {
    if (
      dataFromProps.defaultClickEvents &&
      dataFromProps.defaultClickEvents !== null &&
      !areArraysIdentical(ctrSelectedClicks, dataFromProps.defaultClickEvents)
    ) {
      setCTR_SelectedClicks(dataFromProps.defaultClickEvents);
    }
  }, [dataFromProps.defaultClickEvents]);

  React.useEffect(() => {
    if (order && orderBy && data.formattedRows?.length) {
      const rows_ = orderBy
        ? stableSort(data.rows, getComparator(order, orderBy))
        : data.rows;
      const formattedRows_ = orderBy
        ? stableSort(data.formattedRows, getComparator(order, orderBy))
        : data.formattedRows;
      onLiveDataChange({
        ...data,
        rows: rows_,
        formattedRows: formattedRows_,
      });
    }
  }, [order, orderBy, page, rowsPerPage, data?.formattedRows]);

  const createSortHandler = (property) => (_ev) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = async (event, newPage) => {
    setPage(newPage);
    onPageChange(event, newPage);
  };

  const handleChangeRowsPerPage = async (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    onRowsPerPageChange(event);
  };

  const handleOpenPicker = async (event, index) => {
    setAnchorEl(event.currentTarget);
    setColumnIndex(index);
  };

  const handleClosePicker = async () => {
    setAnchorEl(null);
    setColumnIndex(null);
  };

  const handleSelectViews = async (_ev, newiItems) => {
    setCTR_SelectedViews(newiItems);
  };

  const handleSelectClicks = async (_ev, newiItems) => {
    setCTR_SelectedClicks(newiItems);
  };

  // set selected dimension to show
  const handleSelectDimension = async (
    _pickerType,
    _headerName,
    _selectedDimension,
  ) => {
    setSelectedDimension(_selectedDimension);
    const selectedEvent = {
      metricName: null,
      value: headerMetricSelection?.name || headerMetricSelection,
    };
    const newData = formatRawData({
      viewEvents: ctrSelectedViews,
      clickEvents: ctrSelectedClicks,
      rawData: rawDataFromProps,
      selectedDimension: _selectedDimension,
      selectedEvent,
      withCTR,
    });
    setData(newData);
    handleClosePicker();
  };

  // add metric filter accordingly
  const handleSelectMetric = async (
    _pickerType,
    _headerName,
    _selectedMetric,
  ) => {
    setHeaderMetricSelection(_selectedMetric);
    let newData;

    if (_selectedMetric?.name === null || _selectedMetric === null) {
      newData = formatRawData({
        rawData: rawDataFromProps,
        viewEvents: ctrSelectedViews,
        clickEvents: ctrSelectedClicks,
        selectedDimension,
        selectedEvent: null,
        withCTR: withCTR ?? true,
      });
      setData(newData);
    } else {
      const selectedEvent = {
        metricName: _headerName,
        value: _selectedMetric?.name ?? _selectedMetric,
      };
      newData = formatRawData({
        rawData: rawDataFromProps,
        viewEvents: ctrSelectedViews,
        clickEvents: ctrSelectedClicks,
        selectedDimension,
        selectedEvent,
        withCTR: withCTR ?? true,
      });
      setData(newData);

      if (orderBy !== _headerName) {
        if (order !== 'desc') setOrder('desc');
        setOrderBy(_headerName);
      }
    }

    onLiveDataChange(newData);

    handleClosePicker();
  };

  const hasCTRData = React.useMemo(() => {
    return withCTR !== null ? dataFromProps.hasCTRData || null : true;
  }, [dataFromProps.hasCTRData, withCTR]);

  // Use a single headers array for mapping
  const { headers, metrics } = React.useMemo(() => {
    const headers_ = [selectedDimension, ...metricsFromProps];
    const metrics_ = [...metricsFromProps];
    if (hasCTRData) {
      headers_.push({ name: 'ctr', label: 'CTR', _nonApi: true });
      metrics_.push({ name: 'ctr', label: 'CTR', _nonApi: true });
    }
    return { headers: headers_, metrics: metrics_ };
  }, [selectedDimension, metricsFromProps, hasCTRData]);

  const headersDescriptions = React.useMemo(() => {
    if (!metadata) return {};

    const dimensions_ = {};
    metadata.dimensions.forEach((it) => {
      dimensions_[it.apiName] = it.description;
    });
    const metrics_ = {};
    metadata.metrics.forEach((it) => {
      metrics_[it.apiName] = it.description;
    });
    if (hasCTRData) {
      metrics_.ctr =
        'Click-Through Rate (CTR) is the percentage of clicks per view, calculated by dividing total clicks by total views. When the Show CTR option is active, this data is included in the report. If disabled, click events are ignored during data aggregation.';
    }
    return { dimensions: dimensions_, metrics: metrics_ };
  }, [metadata, hasCTRData]);

  const totalRow = React.useMemo(() => {
    return data.totalRow || null;
  }, [data.totalRow, hasCTRData]);

  const emptyRows = React.useMemo(
    () =>
      page > 0
        ? Math.max(0, (1 + page) * rowsPerPage - data.formattedRows.length)
        : 0,
    [page, rowsPerPage, data?.formattedRows, hasCTRData],
  );

  const dataRows = React.useMemo(() => {
    const data_ = orderBy
      ? stableSort(data.formattedRows, getComparator(order, orderBy))
      : data.formattedRows;
    const sliced = data_.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );

    return sliced;
  }, [order, orderBy, page, rowsPerPage, data.formattedRows, hasCTRData]);

  const metricSelection = React.useMemo(() => {
    return dataFromProps.metricSelection || null;
  }, [dataFromProps.metricSelection]);

  const { viewsItems, clicksItems } = React.useMemo(() => {
    const allEventNames = dataFromProps.allEventNames || [];
    const viewsItems_ = allEventNames.filter(
      (it) => !ctrSelectedClicks.includes(it),
    );
    const clicksItems_ = allEventNames.filter(
      (it) => !ctrSelectedViews.includes(it),
    );
    return { viewsItems: viewsItems_, clicksItems: clicksItems_ };
  }, [dataFromProps.allEventNames, ctrSelectedClicks, ctrSelectedViews]);

  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer component={Paper}>
        <Table stickyHeader aria-label='dynamic analytics table'>
          <TableHead>
            <TableRow>
              {/* empty cell */}
              <TableCell>{'⠀'}</TableCell>

              {headers.map((header, index) => {
                // first item/index is selected dimension
                const pickerType = index === 0 ? 'dimension' : 'metric';
                const items =
                  pickerType === 'dimension'
                    ? dimensions.map((it) => ({
                        ...it,
                        label:
                          metadata?.dimensions?.find?.(
                            (d) => d.apiName === it.name,
                          )?.uiName || it.name,
                      }))
                    : metricSelection?.[header.name]
                    ? [
                        {
                          name: null,
                          label: 'All',
                        },
                        ...metricSelection[header.name],
                      ]
                    : null;
                const headerHasSelection = !!items || header.name === 'ctr';
                const pickerHandler = (event) =>
                  handleOpenPicker(event, index, header, items);
                const selectHandler =
                  pickerType === 'dimension'
                    ? handleSelectDimension
                    : handleSelectMetric;
                const tooltipText =
                  pickerType === 'dimension'
                    ? headersDescriptions.dimensions[header.name]
                    : headersDescriptions.metrics[header.name];

                return (
                  <TableCell
                    key={`${index}:${header.name}`}
                    align={pickerType === 'metric' ? 'right' : 'left'}
                    sortDirection={orderBy === header.name ? order : false}
                    sx={{
                      fontWeight: 'bold',
                      maxWidth:
                        pickerType === 'dimension' ? '333px' : undefined,
                    }}
                  >
                    {!headerHasSelection ? (
                      <Box>
                        {pickerType === 'metric' ? (
                          <TableSortLabel
                            active={orderBy === header.name}
                            direction={orderBy === header.name ? order : 'asc'}
                            onClick={createSortHandler(header.name)}
                          >
                            <HeaderTypography
                              underline
                              variant='subtitle2'
                              tooltip={Boolean(tooltipText)}
                              tooltipText={tooltipText}
                              sx={{ userSelect: 'none' }}
                            >
                              {header.label?.toUpperCase() === 'CTR'
                                ? header.label
                                : changeCase.sentenceCase(header.label)}
                              {orderBy === header.name ? (
                                <Box component='span' sx={visuallyHidden}>
                                  {order === 'desc'
                                    ? 'sorted descending'
                                    : 'sorted ascending'}
                                </Box>
                              ) : null}
                            </HeaderTypography>
                          </TableSortLabel>
                        ) : (
                          <HeaderTypography
                            underline
                            variant='subtitle2'
                            tooltip={Boolean(tooltipText)}
                            tooltipText={tooltipText}
                            sx={{ userSelect: 'none' }}
                          >
                            {header.label?.toUpperCase() === 'CTR'
                              ? header.label
                              : changeCase.sentenceCase(header.label)}
                          </HeaderTypography>
                        )}
                      </Box>
                    ) : null}

                    {headerHasSelection ? (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems:
                            pickerType === 'metric' ? 'center' : undefined,
                          justifyContent: 'center',
                          gap: 1,
                        }}
                      >
                        {pickerType === 'metric' ? (
                          <Box>
                            <TableSortLabel
                              active={orderBy === header.name}
                              direction={
                                orderBy === header.name ? order : 'asc'
                              }
                              onClick={createSortHandler(header.name)}
                            >
                              <HeaderTypography
                                underline
                                variant='subtitle2'
                                tooltip={Boolean(tooltipText)}
                                tooltipText={tooltipText}
                                sx={{ userSelect: 'none' }}
                              >
                                {header.label?.toUpperCase() === 'CTR'
                                  ? header.label
                                  : changeCase.sentenceCase(header.label)}
                                {orderBy === header.name ? (
                                  <Box component='span' sx={visuallyHidden}>
                                    {order === 'desc'
                                      ? 'sorted descending'
                                      : 'sorted ascending'}
                                  </Box>
                                ) : null}
                              </HeaderTypography>
                            </TableSortLabel>
                          </Box>
                        ) : null}

                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                          }}
                        >
                          <Chip
                            clickable
                            variant={
                              pickerType === 'dimension'
                                ? 'outlined'
                                : 'contained'
                            }
                            size={pickerType === 'metric' ? 'small' : undefined}
                            onClick={pickerHandler}
                            onDelete={pickerHandler}
                            deleteIcon={<ArrowDropDownIcon />}
                            label={
                              <Typography
                                variant='subtitle2'
                                sx={{
                                  userSelect: 'none',
                                  fontSize:
                                    pickerType === 'dimension'
                                      ? '0.85rem'
                                      : '0.7rem',
                                }}
                              >
                                {header.name === 'ctr'
                                  ? 'Events'
                                  : changeCase.sentenceCase(
                                      pickerType === 'dimension'
                                        ? metadata?.dimensions?.find?.(
                                            (d) =>
                                              d.apiName ===
                                              selectedDimension.name,
                                          )?.uiName || selectedDimension.name
                                        : headerMetricSelection?.label ||
                                            headerMetricSelection?.name ||
                                            headerMetricSelection ||
                                            'All',
                                    )}
                              </Typography>
                            }
                            sx={{
                              mr:
                                pickerType === 'dimension' ? 'auto' : undefined,
                              p:
                                pickerType === 'dimension' ? '10px' : undefined,
                              borderRadius:
                                pickerType === 'dimension' ? '4px' : '10px',
                              backgroundColor:
                                pickerType === 'dimension'
                                  ? 'inherit'
                                  : undefined,
                              minWidth:
                                pickerType === 'dimension'
                                  ? '200px'
                                  : undefined,
                              minHeight:
                                pickerType === 'dimension' ? '32px' : undefined,
                            }}
                          />

                          {pickerType === 'dimension' ? (
                            <FormControlLabel
                              label='With CTR'
                              control={
                                <Checkbox
                                  checked={withCTR ?? true}
                                  disabled={
                                    !ctrDataEligible || !selectedDimension
                                  }
                                  onChange={(e) =>
                                    setWithCTR(Boolean(e.target.checked))
                                  }
                                  inputProps={{
                                    'aria-label': 'With CTR calculation',
                                  }}
                                />
                              }
                              sx={{ ml: '4px' }}
                            />
                          ) : null}
                        </Box>

                        {anchorEl && columnIndex === index ? (
                          header.name === 'ctr' ? (
                            <CTRMetricPicker
                              anchorEl={anchorEl}
                              onSelectViews={handleSelectViews}
                              onSelectClicks={handleSelectClicks}
                              onClose={handleClosePicker}
                              clickItems={clicksItems}
                              viewItems={viewsItems}
                              currentClicks={ctrSelectedClicks}
                              currentViews={ctrSelectedViews}
                            />
                          ) : (
                            <DimensionMetricPicker
                              anchorEl={anchorEl}
                              pickerType={pickerType}
                              headerName={header.name}
                              items={items}
                              currentItem={header}
                              onSelect={selectHandler}
                              onClose={handleClosePicker}
                            />
                          )
                        ) : null}
                      </Box>
                    ) : null}
                  </TableCell>
                );
              })}
            </TableRow>
            {totalRow ? (
              <TableRow>
                <TableCell>{'⠀'}</TableCell>
                <TableCell sx={{ maxWidth: '333px' }}>
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 'medium', color: 'gray' }}
                  >
                    Total
                  </Typography>
                </TableCell>
                {metrics.map((metric, index) => (
                  <TableCell
                    key={`total-row-${index}:${metric.name}`}
                    align='right'
                  >
                    <ItemValue
                      total
                      nullishText={
                        metric.name?.toLowerCase() === 'ctr' ? 'n/a' : ''
                      }
                      noSecondary={
                        totalRow[metric.name]?.type === 'TYPE_SECONDS'
                      }
                      direction='column'
                      value={
                        (totalRow[metric.name]?.main ?? null) !== null
                          ? {
                              ...totalRow[metric.name],
                              main: formatNumber(totalRow[metric.name].main),
                            }
                          : (totalRow[metric.name] ?? null) !== null
                          ? formatNumber(totalRow[metric.name])
                          : totalRow[metric.name]
                      }
                    />
                  </TableCell>
                ))}
              </TableRow>
            ) : null}
          </TableHead>
          <TableBody>
            {dataRows.map((row, index) => {
              // const originalDimensionValue = row[dimensions?.[0].name];
              // const originalDimensionValue = row.name;

              const originalDimensionValue = row.name;
              const isItemSelected = selectedRowsForChart.some(
                (item) => item.name === originalDimensionValue,
              );
              //
              //
              return (
                <TableRow
                  key={`${index}:${originalDimensionValue}`}
                  sx={{
                    '&:nth-of-type(odd)': {
                      backgroundColor: 'action.hover',
                    },
                    '&:hover': {
                      backgroundColor: 'action.selected',
                    },
                  }}
                >
                  <TableCell padding='checkbox'>
                    <Checkbox
                      checked={isItemSelected}
                      onChange={(e) =>
                        onCheckboxChange(row, Boolean(e.target.checked))
                      }
                      disabled={
                        !isItemSelected &&
                        selectedRowsForChart.length >= MAX_CHART_LINES
                      }
                      inputProps={{
                        'aria-label': `Select ${row.name}`,
                      }}
                      sx={visuallyHidden}
                    />
                  </TableCell>
                  {selectedDimension?.name && (
                    <TableCell align='left' sx={{ maxWidth: '333px' }}>
                      <Typography variant='body2' sx={{ fontWeight: 'medium' }}>
                        {selectedDimension.name?.includes('date')
                          ? (() => {
                              const date = dayjs(
                                row[selectedDimension.name],
                                'YYYYMMDD',
                              );
                              return date.isValid()
                                ? date.format('DD MMMM YYYY')
                                : row[selectedDimension.name];
                            })()
                          : row[selectedDimension.name]}
                      </Typography>
                    </TableCell>
                  )}
                  {metrics.map((metric, index2) => (
                    <TableCell key={`${index2}:${metric.name}`} align='right'>
                      <ItemValue
                        noSecondary={row[metric.name]?.type === 'TYPE_SECONDS'}
                        nullishText={
                          metric.name?.toLowerCase() === 'ctr' ? 'n/a' : ''
                        }
                        value={
                          (row[metric.name]?.main ?? null) !== null
                            ? {
                                ...row[metric.name],
                                main: formatNumber(row[metric.name].main),
                              }
                            : (row[metric.name] ?? null) !== null
                            ? formatNumber(row[metric.name])
                            : row[metric.name]
                        }
                      />
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow>
                <TableCell colSpan={headers.length + 1} />
              </TableRow>
            )}
            {data.rows?.length === 0 && (
              <TableRow>
                <TableCell colSpan={headers.length + 1} align='center'>
                  No data available for this report.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100, { label: 'All', value: -1 }]}
        component='div'
        count={data.formattedRows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ActionsComponent={TablePaginationActions}
        slotProps={{
          select: {
            inputProps: {
              'aria-label': 'rows per page',
            },
          },
        }}
      />
    </Box>
  );
};

export default AnalyticsTable;
